import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, ChevronDown, ChevronUp } from 'lucide-react';
import apiService from '../../../services/api';

interface CountryContext {
  id: number;
  countryCode: string;
  contextType: string;
  title: string;
  content: string;
  importance: number;
  sortOrder: number;
  isActive: boolean;
  country: {
    name: string;
    flagEmoji: string;
  };
}

interface Country {
  code: string;
  name: string;
  flagEmoji: string;
  isActive: boolean;
}

const contextTypes = [
  { value: 'demographics', label: 'Demographics' },
  { value: 'geography', label: 'Geography' },
  { value: 'history', label: 'History' },
  { value: 'politics', label: 'Politics' },
  { value: 'economy', label: 'Economy' },
  { value: 'security', label: 'Security' },
];

interface NotificationState {
  type: 'success' | 'error' | null;
  message: string;
}

export default function CountryContextManager() {
  const [contexts, setContexts] = useState<CountryContext[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<NotificationState>({ type: null, message: '' });
  
  const [formData, setFormData] = useState({
    countryCode: '',
    contextType: 'demographics',
    title: '',
    content: '',
    importance: 5,
    sortOrder: 0,
  });

  // Clear notification after 5 seconds
  useEffect(() => {
    if (notification.type) {
      const timer = setTimeout(() => {
        setNotification({ type: null, message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    fetchCountries();
    fetchContexts();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      fetchContexts(selectedCountry);
    }
  }, [selectedCountry]);

  const fetchCountries = async () => {
    try {
      const response = await apiService.get('/api/countries');
      setCountries(response.data.filter((c: Country) => c.isActive));
      if (response.data.length > 0 && !selectedCountry) {
        setSelectedCountry(response.data[0].code);
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to load countries' });
    }
  };

  const fetchContexts = async (countryCode?: string) => {
    try {
      setLoading(true);
      const params = countryCode ? { countryCode } : {};
      const response = await apiService.get('/api/admin/prompts/contexts', { params });
      setContexts(response.data);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to load contexts' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await apiService.put(`/api/admin/prompts/contexts/${editingId}`, formData);
        setNotification({ type: 'success', message: 'Context updated successfully' });
      } else {
        await apiService.post('/api/admin/prompts/contexts', formData);
        setNotification({ type: 'success', message: 'Context created successfully' });
      }
      
      setEditingId(null);
      setIsCreating(false);
      resetForm();
      fetchContexts(selectedCountry);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to save context' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this context?')) return;
    
    try {
      await apiService.delete(`/api/admin/prompts/contexts/${id}`);
      setNotification({ type: 'success', message: 'Context deleted successfully' });
      fetchContexts(selectedCountry);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to delete context' });
    }
  };

  const handleEdit = (context: CountryContext) => {
    setFormData({
      countryCode: context.countryCode,
      contextType: context.contextType,
      title: context.title,
      content: context.content,
      importance: context.importance,
      sortOrder: context.sortOrder,
    });
    setEditingId(context.id);
    setIsCreating(false);
  };

  const resetForm = () => {
    setFormData({
      countryCode: selectedCountry,
      contextType: 'demographics',
      title: '',
      content: '',
      importance: 5,
      sortOrder: 0,
    });
  };

  const toggleType = (type: string) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedTypes(newExpanded);
  };

  const groupedContexts = contexts.reduce((acc, context) => {
    if (!acc[context.contextType]) {
      acc[context.contextType] = [];
    }
    acc[context.contextType].push(context);
    return acc;
  }, {} as Record<string, CountryContext[]>);

  // Sort contexts within each group
  Object.keys(groupedContexts).forEach(type => {
    groupedContexts[type].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return b.importance - a.importance;
    });
  });

  return (
    <div className="p-6">
      {/* Help Section */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">What are Country Contexts?</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
          Country Contexts provide specific information about each country that the AI uses during analysis:
        </p>
        <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 ml-4">
          <li>• <strong>Demographics</strong>: Population, ethnic composition, languages</li>
          <li>• <strong>Geography</strong>: Borders, strategic locations, terrain</li>
          <li>• <strong>History</strong>: Past conflicts, occupations, key events</li>
          <li>• <strong>Politics</strong>: Government, alliances, international relations</li>
          <li>• <strong>Security</strong>: Vulnerabilities, military capabilities, threat actors</li>
        </ul>
        <p className="text-sm text-gray-900 dark:text-gray-100 mt-3 font-medium">
          This information is injected into prompts as {'{{countryContexts}}'} variable.
        </p>
      </div>

      {/* Notification */}
      {notification.type && (
        <div className={`mb-4 p-4 rounded-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
            : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Country Contexts
          </h3>
          
          {/* Country selector */}
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {countries.map(country => (
              <option key={country.code} value={country.code}>
                {country.flagEmoji} {country.name}
              </option>
            ))}
          </select>
        </div>
        
        <button
          onClick={() => {
            setIsCreating(true);
            setEditingId(null);
            resetForm();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Context
        </button>
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingId) && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Context Type
              </label>
              <select
                value={formData.contextType}
                onChange={(e) => setFormData({ ...formData, contextType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                {contextTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                placeholder="e.g., Russian-speaking minority"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                placeholder="Detailed context information..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Importance (1-10)
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.importance}
                onChange={(e) => setFormData({ ...formData, importance: parseInt(e.target.value) || 5 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => {
                setIsCreating(false);
                setEditingId(null);
                resetForm();
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
          </div>
        </div>
      )}

      {/* Contexts List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading contexts...</div>
      ) : (
        <div className="space-y-4">
          {contextTypes.map(type => {
            const typeContexts = groupedContexts[type.value] || [];
            const isExpanded = expandedTypes.has(type.value);
            
            return (
              <div key={type.value} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <button
                  onClick={() => toggleType(type.value)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {type.label}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      ({typeContexts.length} contexts)
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                
                {isExpanded && typeContexts.length > 0 && (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {typeContexts.map((context, index) => (
                      <div key={context.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {context.title}
                              </h4>
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                                  Importance: {context.importance}/10
                                </span>
                                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                  Order: {context.sortOrder}
                                </span>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {context.content}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => handleEdit(context)}
                              className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(context.id)}
                              className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}